import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import { dateFormatter, request } from '../../../../../utils';
import parse from 'html-react-parser';
import { BASE_URL_FOR_FILE } from '../../../../../utils/BaseUrl';
import { useEffect, useState } from 'react';
import React from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

// ─── Font Registration ────────────────────────────────────────────────────────
Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});
Font.register({
  family: 'Roboto2',
  fonts: [{ src: '/Roboto-Medium.ttf' }]
});
Font.register({
  family: 'Noto',
  fonts: [
    { src: '/NotoSans-Regular.ttf' },
    { src: '/NotoSans-Bold.ttf', fontWeight: 'bold' }
  ]
});

// ─── Design tokens (mirrors DischargeSummaryPDF exactly) ─────────────────────
// Color palette
// #0f172a  — near-black, table headers / strong labels
// #1e293b  — body text default
// #334155  — medium-dark, section sub-labels
// #475569  — muted labels / field keys
// #64748b  — placeholder / legend text
// #cbd5e1  — border color
// #e2e8f0  — row dividers
// #f1f5f9  — section header background
// #f8fafc  — alternating row background / content area background
// #ffffff  — white rows / card backgrounds

// A4 usable width = 595 − 2×20 (page padding) = 555px
// Patient header inner = 555 − 2 (border) = 553px
// Test box inner       = 555 − 2 (border) = 553px
// Results columns (inside test box, no extra padding):
//   TEST 45% = ~248px  VALUE 20% = ~110px  UNIT 15% = ~83px  REF 20% = ~110px
//   These use flex % so they're always bounded to parent width.

const styles = StyleSheet.create({

  // ─── Page ─────────────────────────────────────────────────────────────────
  page: {
    paddingTop: 205,           // letterhead(100) + gap(5) + patient info(~90) + gap(10)
    paddingBottom: 20,
    paddingHorizontal: 20,
    fontFamily: 'Roboto2',
    fontSize: 8,
    color: '#1e293b'
  },

  // ─── Base text ────────────────────────────────────────────────────────────
  text: {
    fontSize: 8,
    color: '#1e293b',
    lineHeight: 1.3,
    fontFamily: 'Roboto2'
  },
  textBold: {
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#0f172a'
  },
  textMuted: {
    fontSize: 7,
    color: '#64748b',
    fontFamily: 'Roboto2'
  },
  textLabel: {
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    flexShrink: 0
  },
  textValue: {
    fontSize: 8,
    color: '#0f172a',
    fontFamily: 'Roboto2',
    flexWrap: 'wrap',
    flex: 1
  },

  // ─── Section card (matches DischargeSummaryPDF Section component) ─────────
  section: {
    marginBottom: 6,
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    backgroundColor: '#ffffff'
  },
  sectionHeader: {
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },

  // ─── Patient header fixed box ─────────────────────────────────────────────
  patientHeaderBox: {
    position: 'absolute',
    top: 105,              // letterhead(100) + 5px gap
    left: 20,
    right: 20,
    flexDirection: 'row',
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    backgroundColor: '#f8fafc',
    paddingVertical: 8,
    paddingHorizontal: 6
  },
  // Column widths inside patient header
  // Total inner = ~555px. Divider borders = 1px each.
  // Col1 = flex:1.8, Col2 = flex:2, Col3 = flex:0.8  (flex proportional)
  patientCol1: {
    flex: 1.8,
    paddingRight: 6,
    borderRightWidth: 0.5,
    borderRightColor: '#cbd5e1'
  },
  patientCol2: {
    flex: 2,
    paddingHorizontal: 8,
    borderRightWidth: 0.5,
    borderRightColor: '#cbd5e1'
  },
  patientCol3: {
    flex: 0.8,
    paddingLeft: 8,
    alignItems: 'center',
    justifyContent: 'flex-start'
  },

  // ─── Patient header field rows ────────────────────────────────────────────
  patientFieldRow: {
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'flex-start'
  },
  patientFieldKey: {
    width: 58,            // fixed label width — prevents overflow
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    flexShrink: 0
  },
  patientFieldColon: {
    width: 10,
    fontSize: 8,
    color: '#475569',
    flexShrink: 0
  },
  patientFieldVal: {
    flex: 1,
    fontSize: 8,
    color: '#0f172a',
    fontFamily: 'Roboto2',
    flexWrap: 'wrap'    // wrap long values, never overflow
  },
  patientName: {
    fontSize: 10,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 5,
    flexWrap: 'wrap'
  },

  // ─── Test result outer box ────────────────────────────────────────────────
  testOuterBox: {
    width: '100%',
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    backgroundColor: '#ffffff',
    marginBottom: 6
  },

  // Category heading row inside test box
  testCategoryText: {
    width: '100%',
    textAlign: 'center',
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#0f172a',
    backgroundColor: '#f1f5f9',
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  testNameText: {
    width: '100%',
    textAlign: 'center',
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#334155',
    paddingVertical: 3,
    paddingHorizontal: 6
  },

  // ─── Results table column header row ─────────────────────────────────────
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#0f172a',
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderTopWidth: 0.5,
    borderTopColor: '#cbd5e1',
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1',
    alignItems: 'center'
  },
  tableHeaderText: {
    color: '#ffffff',
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    fontSize: 7
  },

  // ─── Results columns — fixed % widths to prevent overflow ────────────────
  // Total = 45 + 20 + 15 + 20 = 100%
  // Using % strings keeps them proportional to the container automatically.
  colTest: {
    width: '45%',
    paddingLeft: 4,
    fontSize: 7
  },
  colValue: {
    width: '20%',
    paddingLeft: 2,
    fontSize: 7
  },
  colUnit: {
    width: '15%',
    paddingLeft: 2,
    fontSize: 7
  },
  colRef: {
    width: '20%',
    paddingLeft: 2,
    fontSize: 7
  },

  // ─── Parameter data row ───────────────────────────────────────────────────
  paramRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#f1f5f9'
  },
  paramRowAlt: {
    backgroundColor: '#f8fafc'
  },
  // H/L badge
  hlBadge: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    marginRight: 2,
    flexShrink: 0
  },
  // H = High — red tint
  hlBadgeHigh: {
    color: '#dc2626'       // red-600
  },
  // L = Low — blue tint
  hlBadgeLow: {
    color: '#2563eb'       // blue-600
  },
  // Row background tints
  paramRowHigh: {
    backgroundColor: '#fff5f5'   // very light red
  },
  paramRowLow: {
    backgroundColor: '#eff6ff'   // very light blue
  },

  // ─── Group sub-header ─────────────────────────────────────────────────────
  groupSubHeader: {
    width: '100%',
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#334155',
    paddingLeft: 8,
    paddingVertical: 2,
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    textDecoration: 'underline'
  },

  // ─── Grid table ───────────────────────────────────────────────────────────
  gridTable: {
    width: '100%',
    marginTop: 4,
    marginBottom: 6,
    borderWidth: 0.5,
    borderColor: '#cbd5e1'
  },
  gridRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0'
  },
  gridHeaderCell: {
    backgroundColor: '#f1f5f9',
    padding: 3,
    borderRightWidth: 0.5,
    borderRightColor: '#cbd5e1',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  gridCell: {
    padding: 3,
    borderRightWidth: 0.5,
    borderRightColor: '#cbd5e1',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  gridText: {
    fontSize: 7,
    fontFamily: 'Roboto2',
    color: '#1e293b'
  },
  gridTextBold: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#0f172a'
  },

  // ─── Interpretation / Notes / Comments ───────────────────────────────────
  annotationBlock: {
    marginBottom: 4,
    paddingHorizontal: 8,
    paddingVertical: 3
  },
  annotationLabel: {
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
    color: '#475569',
    textDecoration: 'underline',
    marginBottom: 2
  },
  annotationText: {
    fontSize: 8,
    fontFamily: 'Roboto2',
    color: '#1e293b',
    textAlign: 'justify',
    lineHeight: 1.3,
    flexWrap: 'wrap'
  },

  // ─── Footer ───────────────────────────────────────────────────────────────
  footerContainer: {
    marginTop: 8,
    paddingTop: 8,
    paddingHorizontal: 8,
    paddingBottom: 8,
    borderTopWidth: 0.5,
    borderTopColor: '#cbd5e1',
    borderBottomWidth: 0.5,
    borderBottomColor: '#cbd5e1'
  },
  footerThankRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  footerEndText: {
    fontSize: 8,
    textAlign: 'center',
    color: '#64748b',
    fontFamily: 'Roboto2',
    letterSpacing: 0.5,
    marginBottom: 10
  },
  footerBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%'
  },
  footerPageNum: {
    fontSize: 7,
    fontFamily: 'Roboto2',
    color: '#64748b',
    textAlign: 'center'
  },
  signatureBlock: {
    alignItems: 'center'
  },
  signatureText: {
    fontSize: 8,
    textAlign: 'center',
    color: '#0f172a',
    fontFamily: 'Roboto',
    fontWeight: 'bold'
  },
  signatureSubText: {
    fontSize: 7,
    textAlign: 'center',
    color: '#475569',
    fontFamily: 'Roboto2'
  },

  // ─── HTML parser internal table (distinct names) ──────────────────────────
  htmlTable: {
    width: '100%',
    borderWidth: 0.5,
    borderColor: '#cbd5e1',
    marginTop: 4,
    marginBottom: 4
  },
  htmlTableBody: {
    flexDirection: 'column'
  },
  htmlTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 3,
    paddingHorizontal: 2,
    alignItems: 'flex-start'
  },
  htmlTableCell: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: '#cbd5e1',
    padding: 3
  }
});

// ─── Reusable PDF primitives ──────────────────────────────────────────────────

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={style} {...props}>
    {children}
  </PDFView>
);

// Text primitive: always takes a `content` prop (string) to avoid children overflow issues
const Text = ({ style, content, render, wrap, ...props }) => (
  <PDFText
    style={{ ...styles.text, ...style }}
    render={render}
    wrap={wrap !== false}
    {...props}
  >
    {content}
  </PDFText>
);

// Section card — identical API to DischargeSummaryPDF
const Section = ({ title, children, style }) => (
  <PDFView style={{ ...styles.section, ...style }}>
    <PDFText style={styles.sectionHeader}>{title}</PDFText>
    {children}
  </PDFView>
);

// ─── Main export ──────────────────────────────────────────────────────────────

export const ResultEntryPdf = ({ data, isPublic = false }) => {
  const [imagePreview, setImagePreview] = useState([]);
  const [barcodeImg, setBarcodeImg] = React.useState('');
  const [qrImg, setQrImg] = React.useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [isError, setIsError] = useState(false);

  // ─── Barcode & QR ─────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (data?.header?.report_id) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, data.header.report_id, {
          format: "CODE128",
          displayValue: false,
          height: 40,
          margin: 0
        });
        setBarcodeImg(canvas.toDataURL("image/png"));
      } catch (e) {
        console.error("Barcode generation failed", e);
      }
    }
    let path = window.location.pathname;
    if (!isPublic && path.includes("/print/")) {
      path = path.replace("/print/", "/public/print/");
    }
    const fullUrl = `${window.location.origin}${path}${window.location.search}`;
    QRCode.toDataURL(fullUrl).then(setQrImg);
  }, [data, isPublic]);

  // ─── PDF download ─────────────────────────────────────────────────────────
  const downloadPDF = async () => {
    try {
      setIsDownloading(true);
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(PdfDoc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Report-${data?.header?.report_id || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setIsDownloading(false);
        setIsDownloaded(true);
      }, 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    if (isPublic && data && barcodeImg && qrImg && !isDownloading && !isDownloaded) {
      const timer = setTimeout(() => { downloadPDF(); }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPublic, data, barcodeImg, qrImg, isDownloading, isDownloaded]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return String(html || '');
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  };

  const stripHtmlForPdf = (html) => {
    if (!html || typeof html !== 'string') return String(html || '');
    let text = html.replace(/<\/(p|h[1-6]|div|li|tr)>/ig, '\n');
    text = text.replace(/<br\s*\/?>/ig, '\n');
    text = text.replace(/<[^>]*>/g, '');
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    return text.replace(/\n\s*\n/g, '\n\n').trim();
  };

  // Returns { status: 'H'|'L'|'', isHigh: bool, isLow: bool }
  const getValueStatus = (parameter) => {
    const resultValue = parseFloat(parameter?.value);
    const referenceRange = parameter?.reference_range;
    if (!referenceRange || isNaN(resultValue)) return { status: '', isHigh: false, isLow: false };
    const nums = referenceRange.match(/\d+\.?\d*/g);
    if (!nums || nums.length < 2) return { status: '', isHigh: false, isLow: false };
    const from = parseFloat(nums[0]);
    const to = parseFloat(nums[1]);
    if (resultValue > to) return { status: 'H', isHigh: true, isLow: false };
    if (resultValue < from) return { status: 'L', isHigh: false, isLow: true };
    return { status: '', isHigh: false, isLow: false };
  };

  // Keep legacy name for any other usage
  const getStatusIndicator = (parameter) => getValueStatus(parameter).status;

  // ─── Doctor signature fetch ───────────────────────────────────────────────
  const fetchLabAuthorityDoctor = async () => {
    try {
      let signatureDocs = [];
      if (data?.footer?.verified_by) {
        const techRes = await request(
          `/technicians/${data.footer.verified_by}`,
          { method: 'GET' },
          true
        );
        if (techRes.success && techRes.data?.doctors) {
          signatureDocs = techRes.data.doctors;
        }
      }
      if (signatureDocs.length === 0 && data?.footer?.verified_for_doctor) {
        const res = await request('/lab-authority-doctor-list', { method: 'GET' });
        if (res.success) signatureDocs = res.data;
      }
      if (signatureDocs.length > 0) {
        if (data?.footer?.verified_for_doctor) {
          const exact = signatureDocs.filter(i => i._id === data.footer.verified_for_doctor);
          if (exact.length > 0) { setImagePreview(exact); return; }
        }
        setImagePreview([signatureDocs[0]]);
      }
    } catch (e) {
      console.error('Error fetching lab authority doctor:', e);
    }
  };

  useEffect(() => {
    if (data?.footer?.verified_by || data?.footer?.verified_for_doctor) {
      fetchLabAuthorityDoctor();
    }
  }, [data]);

  // ─── Grid renderer ────────────────────────────────────────────────────────
  const renderGrid = (parameter) => {
    const config = parameter.grid_config;
    const value = typeof parameter.value === 'object' ? parameter.value : {};
    if (!config?.rows || !config?.columns) return null;

    return (
      <PDFView style={styles.gridTable}>
        {/* Header */}
        <PDFView style={styles.gridRow}>
          <PDFView style={styles.gridHeaderCell}>
            <PDFText style={styles.gridTextBold}>Label</PDFText>
          </PDFView>
          {config.columns.map((col, i) => (
            <PDFView key={i} style={styles.gridHeaderCell}>
              <PDFText style={styles.gridTextBold}>{col}</PDFText>
            </PDFView>
          ))}
        </PDFView>
        {/* Data rows */}
        {config.rows.map((row, i) => (
          <PDFView key={i} style={styles.gridRow}>
            <PDFView style={styles.gridCell}>
              <PDFText style={styles.gridTextBold}>{row}</PDFText>
            </PDFView>
            {config.columns.map((col, j) => (
              <PDFView key={j} style={styles.gridCell}>
                <PDFText style={styles.gridText}>{value?.[row]?.[col] || '-'}</PDFText>
              </PDFView>
            ))}
          </PDFView>
        ))}
      </PDFView>
    );
  };

  // ─── Group tests by category ──────────────────────────────────────────────
  const groupedTests = [];
  (data?.tests || []).forEach(test => {
    const cat = test.category || 'OTHER';
    const existing = groupedTests.find(g => g.category === cat);
    if (existing) {
      existing.tests.push(test);
    } else {
      groupedTests.push({ category: cat, tests: [test] });
    }
  });

  // ─── PDF Document ─────────────────────────────────────────────────────────
  const PdfDoc = (
    <Document title={`Result Entry - ${data?.header?.report_id}`}>
      {groupedTests.map((group, groupIndex) => (
        <Page size="A4" key={groupIndex}>

          {/* ══ Lab Letterhead Header (fixed — appears on every page) ═════════ */}
          <PDFView fixed style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
            <PDFImage
              src="/logo_Hospital_mM.jpeg"
              style={{ width: '100%', height: 100 }}
            />
          </PDFView>

          {/* ══ Patient Info Header (fixed — appears on every page) ══════════ */}
          <PDFView fixed style={styles.patientHeaderBox}>

            {/* Col 1 — Patient details */}
            <PDFView style={styles.patientCol1}>
              <PDFText style={styles.patientName}>
                {data?.patient?.name || '-'}
              </PDFText>

              {/* Age / Sex */}
              <PDFView style={styles.patientFieldRow}>
                <PDFText style={styles.patientFieldKey}>Age / Sex</PDFText>
                <PDFText style={styles.patientFieldColon}>:</PDFText>
                <PDFText style={styles.patientFieldVal}>
                  {`${data?.patient?.age || '-'} Yrs / ${data?.patient?.gender === 'Male' ? 'M'
                      : data?.patient?.gender === 'Female' ? 'F'
                        : data?.patient?.gender || '-'
                    }`}
                </PDFText>
              </PDFView>

              {/* Referred by */}
              <PDFView style={styles.patientFieldRow}>
                <PDFText style={styles.patientFieldKey}>Referred by</PDFText>
                <PDFText style={styles.patientFieldColon}>:</PDFText>
                <PDFText style={styles.patientFieldVal}>
                  {data?.doctor?.name || '-'}
                </PDFText>
              </PDFView>

              {/* UHID — only render when value is non-empty */}
              {data?.patient?.uhid?.trim() && (
                <PDFView style={styles.patientFieldRow}>
                  <PDFText style={styles.patientFieldKey}>UHID</PDFText>
                  <PDFText style={styles.patientFieldColon}>:</PDFText>
                  <PDFText style={{ ...styles.patientFieldVal, fontFamily: 'Roboto', fontWeight: 'bold', color: '#0f172a' }}>
                    {data.patient.uhid}
                  </PDFText>
                </PDFView>
              )}

              {/* IPD No. — only render when value is non-empty (guards against "" too) */}
              {data?.patient?.ipd_no?.trim() && (
                <PDFView style={styles.patientFieldRow}>
                  <PDFText style={styles.patientFieldKey}>IPD No.</PDFText>
                  <PDFText style={styles.patientFieldColon}>:</PDFText>
                  <PDFText style={{ ...styles.patientFieldVal, fontFamily: 'Roboto', fontWeight: 'bold', color: '#0f172a' }}>
                    {data.patient.ipd_no}
                  </PDFText>
                </PDFView>
              )}

              {/* Lab No. */}
              <PDFView style={styles.patientFieldRow}>
                <PDFText style={styles.patientFieldKey}>Lab No.</PDFText>
                <PDFText style={styles.patientFieldColon}>:</PDFText>
                <PDFText style={{ ...styles.patientFieldVal, fontFamily: 'Roboto', fontWeight: 'bold', color: '#0f172a' }}>
                  {data?.header?.report_id || '-'}
                </PDFText>
              </PDFView>
            </PDFView>

            {/* Col 2 — Barcode + dates */}
            <PDFView style={styles.patientCol2}>
              {barcodeImg && (
                <PDFView style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                  <PDFImage src={barcodeImg} style={{ width: 100, height: 28 }} />
                  <PDFText style={{ ...styles.textMuted, marginLeft: 4, marginTop: 14 }}>
                    {data?.header?.report_id?.slice(-5) || ''}
                  </PDFText>
                </PDFView>
              )}

              {/* Reported on */}
              <PDFView style={styles.patientFieldRow}>
                <PDFText style={styles.patientFieldKey}>Reported on</PDFText>
                <PDFText style={styles.patientFieldColon}>:</PDFText>
                <PDFText style={styles.patientFieldVal}>
                  {data?.formatted_dates?.printed_date || dateFormatter(data?.header?.printed_date) || '-'}
                </PDFText>
              </PDFView>
            </PDFView>

            {/* Col 3 — QR code */}
            <PDFView style={styles.patientCol3}>
              <PDFText style={{ ...styles.textMuted, marginBottom: 3, textAlign: 'center' }}>
                Scan to download
              </PDFText>
              {qrImg && (
                <PDFImage src={qrImg} style={{ width: 52, height: 52 }} />
              )}
            </PDFView>
          </PDFView>

          {/* ══ Test Results ═════════════════════════════════════════════════ */}
          {group.tests.map((test, testIndex) => (
            <PDFView
              key={testIndex}
              // FIX: single spread object — no array style syntax
              style={{ ...styles.testOuterBox, marginTop: testIndex === 0 ? 0 : 6 }}
            >
              {/* Category label — only on first test of a group */}
              {testIndex === 0 && group.category !== 'OTHER' && (
                <PDFText style={styles.testCategoryText}>
                  {group.category.toUpperCase()}
                </PDFText>
              )}

              {/* Test name */}
              <PDFText style={styles.testNameText}>
                {test.test_name?.toUpperCase()}
              </PDFText>

              {/* Column header row — hidden when ALL parameters are grid-only */}
              {!(test.results?.every(g => g.parameters?.every(p => p.is_grid))) && (
                <PDFView style={styles.tableHeaderRow}>
                  <PDFText style={{ ...styles.colTest, ...styles.tableHeaderText }}>TEST</PDFText>
                  <PDFText style={{ ...styles.colValue, ...styles.tableHeaderText }}>VALUE</PDFText>
                  <PDFText style={{ ...styles.colUnit, ...styles.tableHeaderText }}>UNIT</PDFText>
                  <PDFText style={{ ...styles.colRef, ...styles.tableHeaderText }}>REFERENCE</PDFText>
                </PDFView>
              )}

              {/* Parameter groups — sorted by group_order */}
              {[...(test.results || [])]
                .sort((a, b) => (a.group_order || 999) - (b.group_order || 999))
                .map((resultGroup, rgIndex) => {
                  // Alternate row shading resets per group
                  let altCounter = 0;

                  return (
                    <PDFView key={rgIndex} style={{ marginBottom: 0 }}>

                      {/* Group sub-header */}
                      {resultGroup.group_name ? (
                        <PDFText style={styles.groupSubHeader}>
                          {resultGroup.group_name.toUpperCase()}
                        </PDFText>
                      ) : null}

                      {/* Parameters */}
                      {resultGroup.parameters?.map((parameter, pIdx) => {
                        const isGridParagraph =
                          parameter.is_grid &&
                          (parameter.input_type === 'paragraph' ||
                            parameter.input_type === 'document');

                        const { status, isHigh, isLow } = getValueStatus(parameter);
                        const isAbnormal = status !== '';

                        // Resolve display value safely
                        const displayValue = (() => {
                          if (parameter.is_grid) {
                            return typeof parameter.value === 'object'
                              ? (parameter.value?._summary || '')
                              : (parameter.value || parameter.display_value || '-');
                          }
                          if (parameter.input_type === 'paragraph' || parameter.input_type === 'document') {
                            return stripHtml(parameter.value || '-');
                          }
                          return String(parameter.value || parameter.display_value || '-');
                        })();

                        // Row background: H=light red, L=light blue, normal=alternating white/grey
                        const normalBg = altCounter++ % 2 === 0 ? '#ffffff' : '#f8fafc';
                        const rowBg = isHigh ? '#fff5f5' : isLow ? '#eff6ff' : normalBg;

                        // Text color: H=red, L=blue, normal=body color
                        const abnormalTextColor = isHigh ? '#dc2626' : isLow ? '#2563eb' : '#1e293b';
                        const badgeColor = isHigh ? '#dc2626' : '#2563eb';

                        return (
                          <PDFView key={pIdx} style={{ width: '100%' }}>
                            {isGridParagraph ? (
                              // Grid + paragraph layout
                              <PDFView style={{ width: '100%', marginBottom: 4, paddingHorizontal: 6, paddingVertical: 4 }}>
                                <PDFText style={{
                                  fontSize: 8,
                                  fontFamily: 'Roboto',
                                  fontWeight: 'bold',
                                  color: '#334155',
                                  textAlign: 'center',
                                  marginBottom: 3
                                }}>
                                  {parameter.parameter_name || '-'}
                                </PDFText>
                                {renderGrid(parameter)}
                                {typeof parameter.value === 'object' && parameter.value?._summary && (
                                  <PDFView style={{ marginTop: 3, paddingHorizontal: 4 }}>
                                    <PDFText style={{
                                      fontSize: 8,
                                      fontFamily: 'Roboto2',
                                      color: '#1e293b',
                                      textAlign: 'justify',
                                      lineHeight: 1.3,
                                      flexWrap: 'wrap'
                                    }}>
                                      {stripHtmlForPdf(parameter.value._summary)}
                                    </PDFText>
                                  </PDFView>
                                )}
                              </PDFView>
                            ) : (
                              <>
                                {/*
                                  ✅ FIX: single spread object for row background.
                                  Every column uses a strict % width so nothing overflows.
                                  Text nodes always have flexWrap:'wrap' via styles.text.
                                */}
                                <PDFView style={{ ...styles.paramRow, backgroundColor: rowBg }}>

                                  {/* TEST column — 45%
                                      H: red bold  |  L: blue bold  |  normal: body colour */}
                                  <PDFView style={styles.colTest}>
                                    <PDFText style={{
                                      fontSize: 8,
                                      fontFamily: isAbnormal ? 'Roboto' : 'Roboto2',
                                      fontWeight: isAbnormal ? 'bold' : 'normal',
                                      color: isAbnormal ? abnormalTextColor : '#1e293b',
                                      paddingLeft: resultGroup.group_name ? 12 : 4,
                                      flexWrap: 'wrap'
                                    }} wrap>
                                      {parameter.parameter_name || '-'}
                                    </PDFText>
                                  </PDFView>

                                  {/* VALUE column — 20%
                                      H/L badge coloured red/blue, value text also coloured */}
                                  <PDFView style={{ ...styles.colValue, flexDirection: 'row', alignItems: 'flex-start' }}>
                                    {isAbnormal && (
                                      <PDFText style={{ ...styles.hlBadge, color: badgeColor }}>
                                        {status}
                                      </PDFText>
                                    )}
                                    <PDFText style={{
                                      flex: 1,
                                      fontSize: 8,
                                      fontFamily: isAbnormal ? 'Roboto' : 'Roboto2',
                                      fontWeight: isAbnormal ? 'bold' : 'normal',
                                      color: isAbnormal ? abnormalTextColor : '#1e293b',
                                      flexWrap: 'wrap'
                                    }} wrap>
                                      {displayValue}
                                    </PDFText>
                                  </PDFView>

                                  {/* UNIT column — 15% */}
                                  <PDFText style={{
                                    ...styles.colUnit,
                                    fontSize: 8,
                                    fontFamily: 'Noto',
                                    color: isAbnormal ? abnormalTextColor : '#475569',
                                    flexWrap: 'wrap'
                                  }} wrap>
                                    {parameter.unit || '-'}
                                  </PDFText>

                                  {/* REFERENCE column — 20% */}
                                  <PDFText style={{
                                    ...styles.colRef,
                                    fontSize: 8,
                                    fontFamily: 'Noto',
                                    color: '#475569',
                                    flexWrap: 'wrap'
                                  }} wrap>
                                    {parameter.reference_range || '-'}
                                  </PDFText>
                                </PDFView>

                                {/* Grid (non-paragraph type) rendered below its row */}
                                {parameter.is_grid && !isGridParagraph && (
                                  <PDFView style={{ paddingHorizontal: 6 }}>
                                    {renderGrid(parameter)}
                                  </PDFView>
                                )}
                              </>
                            )}
                          </PDFView>
                        );
                      })}
                    </PDFView>
                  );
                })}

              {/* Interpretation / Notes / Comments */}
              {(test.interpretation || test.notes || test.comments) && (
                <PDFView style={{ paddingVertical: 4, paddingHorizontal: 8, borderTopWidth: 0.5, borderTopColor: '#e2e8f0' }}>
                  {test.interpretation && (
                    <PDFView style={styles.annotationBlock}>
                      <PDFText style={styles.annotationLabel}>Interpretation:</PDFText>
                      <PDFText style={styles.annotationText}>{stripHtmlForPdf(test.interpretation)}</PDFText>
                    </PDFView>
                  )}
                  {test.notes && (
                    <PDFView style={styles.annotationBlock}>
                      <PDFText style={styles.annotationLabel}>Notes:</PDFText>
                      <PDFText style={styles.annotationText}>{stripHtmlForPdf(test.notes)}</PDFText>
                    </PDFView>
                  )}
                  {test.comments && (
                    <PDFView style={styles.annotationBlock}>
                      <PDFText style={styles.annotationLabel}>Comments:</PDFText>
                      <PDFText style={styles.annotationText}>{stripHtmlForPdf(test.comments)}</PDFText>
                    </PDFView>
                  )}
                </PDFView>
              )}
            </PDFView>
          ))}

          {/* ══ Footer ═══════════════════════════════════════════════════════ */}
          <PDFView style={styles.footerContainer}>
            {/* Thanks line */}
            <PDFView style={styles.footerThankRow}>
              <PDFText style={{ fontSize: 8, color: '#1e293b', fontFamily: 'Roboto2' }}>
                Thanks For Reference
              </PDFText>
            </PDFView>
            {/* End of report */}
            <PDFText style={styles.footerEndText}>
              ——— End of Report ———
            </PDFText>

            {/* Bottom row: spacer | page numbers | doctor signature */}
            <PDFView style={styles.footerBottomRow}>
              <PDFView style={{ flex: 1 }} />

              {/* Page numbers */}
              <PDFView style={{ flex: 1, alignItems: 'center' }}>
                <PDFText
                  render={({ pageNumber, totalPages }) =>
                    `Page ${pageNumber} of ${totalPages}`
                  }
                  style={styles.footerPageNum}
                />
              </PDFView>

              {/* Doctor signature */}
              <PDFView style={{ flex: 1, alignItems: 'flex-end' }}>
                {imagePreview && imagePreview.length > 0 && (
                  <PDFView style={styles.signatureBlock}>
                    {imagePreview[0]?.signature && (
                      <PDFImage
                        src={`${BASE_URL_FOR_FILE}/${imagePreview[0]?.signature}`}
                        style={{ width: 50, height: 50, marginBottom: 4 }}
                      />
                    )}
                    <PDFText style={styles.signatureText}>
                      {imagePreview[0]?.doctor}
                    </PDFText>
                    {imagePreview[0]?.heading_1 && (
                      <PDFText style={styles.signatureText}>
                        {imagePreview[0].heading_1}
                      </PDFText>
                    )}
                    {imagePreview[0]?.heading_2 && (
                      <PDFText style={styles.signatureSubText}>
                        {imagePreview[0].heading_2}
                      </PDFText>
                    )}
                    {imagePreview[0]?.heading_3 && (
                      <PDFText style={styles.signatureSubText}>
                        {imagePreview[0].heading_3}
                      </PDFText>
                    )}
                  </PDFView>
                )}
              </PDFView>
            </PDFView>
          </PDFView>

        </Page>
      ))}
    </Document>
  );

  // ─── Public download view (unchanged from original) ───────────────────────
  if (isPublic) {
    return (
      <div style={{
        backgroundColor: "white",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "sans-serif"
      }}>
        <div style={{ marginBottom: "30px" }}>
          <img src="/logo_Hospital_m.jpeg" alt="Hospital Logo"
            style={{ width: "200px", height: "80px", objectFit: "contain" }} />
        </div>

        {isDownloaded ? (
          <div style={{
            padding: "30px", backgroundColor: "#f0f9f0", borderRadius: "8px",
            maxWidth: "400px", border: "1px solid #4CAF50", textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", color: "#4CAF50", marginBottom: "15px" }}>✓</div>
            <h2 style={{ color: "#2E7D32", marginBottom: "10px", marginTop: 0 }}>Download Complete</h2>
            <p style={{ color: "#4CAF50", margin: 0, fontSize: "14px" }}>
              Your laboratory report has been downloaded successfully.
            </p>
          </div>
        ) : isError ? (
          <div style={{
            padding: "30px", backgroundColor: "#fff0f0", borderRadius: "8px",
            maxWidth: "400px", border: "1px solid #f44336", textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", color: "#f44336", marginBottom: "15px" }}>✗</div>
            <h2 style={{ color: "#c62828", marginBottom: "10px", marginTop: 0 }}>Download Failed</h2>
            <p style={{ color: "#f44336", margin: 0, fontSize: "14px" }}>
              There was an error generating your report. Please try scanning the QR code again.
            </p>
          </div>
        ) : (
          <div style={{
            padding: "30px", backgroundColor: "#f8f9fa", borderRadius: "8px",
            maxWidth: "400px", border: "1px solid #dee2e6", textAlign: "center"
          }}>
            <div style={{
              width: "40px", height: "40px", border: "3px solid #f3f3f3",
              borderTop: "3px solid #1976d2", borderRadius: "50%",
              animation: "spin 1s linear infinite", margin: "0 auto 20px"
            }} />
            <h2 style={{ color: "#1976d2", marginBottom: "10px", marginTop: 0 }}>Generating Report</h2>
            <p style={{ color: "#6c757d", margin: 0, fontSize: "14px" }}>
              Please wait while we prepare your laboratory report securely...
            </p>
            <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
          </div>
        )}
      </div>
    );
  }

  // ─── Normal viewer ────────────────────────────────────────────────────────
  return (
    <PDFViewer width="100%" height="100%" showToolbar>
      {PdfDoc}
    </PDFViewer>
  );
};
